import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const SideBar = () => {
  const nav = useNavigate();
  const location = useLocation();
  const scrollRef = useRef(null);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [userRole] = useState(() => {
    try {
      const role = window.localStorage.getItem("role");
      return role ? Number(role) : null;
    } catch (e) {
      console.error("Unable to access localStorage:", e);
      return null;
    }
  });

  const getActiveClass = (path) => (location.pathname === path ? "active" : "");

  const studentLinks = [
    { path: "/dashboard-page", label: "Dashboard", icon: "icon-dashboard.svg" },
    { path: "/airachat", label: "Aira Chat", icon: "icon-aira-chat.svg" },
    { path: "/coding", label: "Code Practice", icon: "icon-code-practice.svg" },
    {
      path: "/test",
      label: "Assessment",
      icon: "icon-test.svg",
      secondRow: true,
    },
    {
      path: "/uploads",
      label: "Uploads",
      icon: "icon-analyse.svg",
      secondRow: true,
    },
    {
      path: "/notice-board-student",
      label: "Notice Board",
      icon: "icon-test.svg",
    },
    // { path: "/flashcard", label: "Flashcard", icon: "icon-flashcards.svg", secondRow: true },
    { path: "/dynamic-links", label: "Analytics", icon: "icon-test.svg" },
  ];

  const teacherLinks = [
    { path: "/teacher", label: "Resource", icon: "icon-dashboard.svg" },
    { path: "/coding", label: "Code Practice", icon: "icon-code-practice.svg" },
    {
      path: "/create-quiz",
      label: "Create",
      icon: "/svgviewer-output 1.svg",
      secondRow: true,
    },
    { path: "/notice-board", label: "Notice Board", icon: "icon-test.svg" },
    // { path: "/test", label: "Assessment", icon: "icon-test.svg", secondRow: true },
    // { path: "/prep-selection", label: "CE", icon: "icon-competitive-exam.svg" },
    { path: "/airachat", label: "Aira Chat", icon: "icon-aira-chat.svg" },
    { path: "/report", label: "Report", icon: "icon-analyse.svg" },
    { path: "/cohort", label: "Cohort", icon: "Group.png"},
  ];

  const adminLinks = [
    { path: "/people", label: "People", icon: "icon-dashboard.svg" },
    { path: "/coding", label: "Code Practice", icon: "icon-code-practice.svg" },
    { path: "/admin-cohort", label: "Cohort", icon: "Group.png" },
    { path: "/airachat", label: "Aira Chat", icon: "icon-aira-chat.svg" },
    {
      path: "/admin-report",
      label: "Report",
      icon: "icon-analyse.svg",
      secondRow: true,
    },
  ];

  const linksToRender =
    userRole === 1
      ? studentLinks
      : userRole === 2
        ? teacherLinks
        : userRole === 3
          ? adminLinks
          : studentLinks;

  return (
    <aside className="sidebar-floating-capsule">
      <style>{`
        .sidebar-floating-capsule {
          background: #000;
          margin: 20px 0 20px 15px; 
          height: calc(100% - 40px);
          width: 75px;
          min-width: 75px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          border-radius: 30px;
          overflow: hidden;
          box-shadow: 0 15px 35px rgba(0,0,0,0.6);
          position: relative;
          z-index: 10;
        }

        .menu-scroll-container {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 20px 0;
          margin: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px; 
          scroll-behavior: smooth;
        }

        .menu-scroll-container::-webkit-scrollbar {
          width: 3px;
        }
        .menu-scroll-container::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 10px;
        }
        .menu-scroll-container:hover::-webkit-scrollbar-thumb {
          background: #333;
        }

        .sidebar-floating-capsule .nav-page-link {
          display: flex;
          flex-direction: column; 
          align-items: center;
          justify-content: center;
          text-decoration: none;
          width: 100%;
          padding: 10px 0;
          // color: #777;
          color: white;
          transition: all 0.3s ease;
          position: relative;
          cursor: pointer;
        }

        .sidebar-floating-capsule .nav-page-link:hover {
          color: #fff;
          background: rgba(255, 255, 255, 0.05);
        }

        .sidebar-floating-capsule .nav-page-link.active {
          color: #32cd32;
          background: rgba(50, 205, 50, 0.08);
        }

        .sidebar-floating-capsule .nav-page-link.active::before {
          content: "";
          position: absolute;
          left: 0;
          top: 20%;
          height: 60%;
          width: 3px;
          background: #32cd32;
          border-radius: 0 4px 4px 0;
        }

        .sidebar-floating-capsule .nav-page-link img {
          width: 20px;
          height: 20px;
          margin-bottom: 3px;
          filter: grayscale(1) brightness(0.7);
          transition: 0.3s;
        }

        .sidebar-floating-capsule .nav-page-link.active img {
          filter: brightness(1);
        }

        .link-label {
          font-size: 9px;
          font-weight: 300;
          text-align: center;
          line-height: 1.2;
          padding: 0 4px;
          letter-spacing: 0.2px;
        }

        @media (max-width: 992px) {
          .sidebar-floating-capsule {
            width: 100% !important;
            height: 70px !important;
            margin: 0 !important;
            border-radius: 20px 20px 0 0;
            position: fixed;
            bottom: 0;
            left: 0;
          }
          .menu-scroll-container {
            flex-direction: row;
            padding: 0 10px !important;
            align-items: center;
            gap: 5px;
            overflow-x: auto;
            scrollbar-width: none;
          }
          .menu-scroll-container::-webkit-scrollbar { display: none; }

          .sidebar-floating-capsule .nav-page-link {
            min-width: 75px;
            padding: 0 !important;
          }
          .sidebar-floating-capsule .nav-page-link.active::before {
            width: 30%;
            height: 3px;
            bottom: 0;
            top: auto;
            left: 35%;
            border-radius: 4px 4px 0 0;
          }
          .link-label {
            font-size: 9px;
            margin-top: 2px;
          }
        }
      `}</style>

      <ul ref={scrollRef} className="menu-scroll-container">
        {linksToRender.map((link, idx) => (
          <li key={idx} style={{ width: "100%" }}>
            <a
              href="javascript:void(0)"
              className={`nav-page-link ${getActiveClass(link.path)}`}
              onClick={() => nav(link.path)}
            >
              <img
                src={`./assets/images/inner-pages/icons/${link.icon}`}
                alt=""
              />
              <span className="link-label">{link.label}</span>
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
};

export default SideBar;
